import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AppShell from '../components/home/AppShell';
import { Users, Coffee, CheckCircle2, Clock, Activity, Zap, Gauge, X, Radio, Monitor, Plane, Thermometer, Check } from 'lucide-react';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  working: { dot: 'bg-emerald-500', label: 'Working', chip: 'text-emerald-700 bg-emerald-50' },
  on_break: { dot: 'bg-amber-500', label: 'On Break', chip: 'text-amber-700 bg-amber-50' },
  finished: { dot: 'bg-slate-400', label: 'Finished', chip: 'text-slate-600 bg-slate-100' },
  off: { dot: 'bg-slate-300', label: 'Off', chip: 'text-slate-500 bg-slate-100' },
};
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }) : '—');
const initials = (n) => (n || 'U').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

const Workforce = () => {
  const { token } = useAuth();
  const [overview, setOverview] = useState(null);
  const [feed, setFeed] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const h = { Authorization: `Bearer ${token}` };
      const [o, f] = await Promise.all([
        fetch(`${API}/api/workforce/admin/overview`, { headers: h }).then((r) => r.json()),
        fetch(`${API}/api/workforce/admin/live-feed`, { headers: h }).then((r) => r.json()),
      ]);
      setOverview(o); setFeed(f.feed || []);
    } catch { /* keep last */ }
  }, [token]);

  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);

  const sum = overview?.summary;

  return (
    <AppShell activeKey="workforce">
      <div className="max-w-[1200px] mx-auto px-6 sm:px-8 py-7" data-testid="workforce-page">
        <div className="flex items-center gap-2.5">
          <h1 className="text-[26px] sm:text-[30px] font-bold tracking-tight">Workforce</h1>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full" data-testid="workforce-live-badge">
            <Radio className="w-3 h-3" /> Live
          </span>
        </div>
        <p className="text-[13px] text-slate-400 mt-1">Your onboarding team today — at a glance.</p>

        {/* Executive summary */}
        {sum && (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="workforce-summary">
            <SummaryCard icon={Zap} label="Working Now" value={sum.working} tone="emerald" />
            <SummaryCard icon={Coffee} label="On Break" value={sum.on_break} tone="amber" />
            <SummaryCard icon={CheckCircle2} label="Finished" value={sum.finished} tone="slate" />
            <SummaryCard icon={Clock} label="Total Hours Today" value={sum.total_hours_label} tone="blue" />
            <SummaryCard icon={Activity} label="Active Time" value={sum.active_hours_label} tone="violet" />
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Team table */}
          <section className="lg:col-span-2 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="workforce-team">
            <h2 className="text-[15px] font-semibold px-5 pt-4 pb-3 border-b border-slate-100">Team Members</h2>
            <div className="divide-y divide-slate-100">
              {(overview?.employees || []).map((e) => {
                const m = STATUS_META[e.status] || STATUS_META.off;
                return (
                  <button
                    key={e.user_id}
                    type="button"
                    onClick={() => setSelected(e.user_id)}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-slate-50 transition-colors"
                    data-testid={`workforce-employee-row-${e.user_id}`}
                  >
                    <span className="relative w-9 h-9 rounded-full bg-gradient-to-br from-[#5A8BFF] to-[#3A6AFF] flex items-center justify-center text-[12px] font-bold text-white shrink-0">
                      {e.avatar_url ? <img src={e.avatar_url} alt={e.user_name} className="w-9 h-9 rounded-full object-cover" /> : initials(e.user_name)}
                      <span className={cn('absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white', m.dot)} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13.5px] font-semibold text-slate-800 truncate">{e.user_name}</p>
                      <p className="text-[11.5px] text-slate-400 truncate">
                        {e.status === 'off' ? 'Not checked in' : (e.current_module ? `${e.current_module}${e.current_client ? ` · ${e.current_client}` : ''}` : m.label)}
                      </p>
                    </div>
                    <div className="hidden sm:flex items-center gap-5 text-[12px] text-slate-500 shrink-0">
                      <Col label="Started" value={fmtTime(e.checked_in_at)} />
                      <Col label="Session" value={e.session_label} />
                      <Col label="Break" value={e.break_label} />
                      <Col label="Active" value={e.active_label} />
                    </div>
                    <span className={cn('text-[11px] font-semibold px-2 py-1 rounded-full shrink-0', m.chip)} data-testid={`workforce-status-${e.user_id}`}>{m.label}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Live feed */}
          <section className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="workforce-live-feed">
            <h2 className="text-[15px] font-semibold px-5 pt-4 pb-3 border-b border-slate-100 flex items-center gap-2">
              <Radio className="w-4 h-4 text-emerald-500" /> Live Activity
            </h2>
            <div className="p-4 space-y-2.5 max-h-[520px] overflow-y-auto">
              {feed.length === 0 && <p className="text-[13px] text-slate-400 px-1">No activity yet today.</p>}
              {feed.map((f, i) => (
                <div key={i} className="flex items-start gap-2.5 text-[12.5px]" data-testid="live-feed-row">
                  <span className="text-[11px] tabular-nums text-slate-400 w-14 shrink-0 pt-0.5">{fmtTime(f.at)}</span>
                  <p className="text-slate-700"><b className="font-semibold">{f.user_name}</b> · {f.label}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        <AdminLeavePanel token={token} />
      </div>

      {selected && <EmployeeDrawer userId={selected} onClose={() => setSelected(null)} token={token} />}
    </AppShell>
  );
};

const LEAVE_STATUS = {
  pending: 'text-amber-700 bg-amber-50 border-amber-200',
  approved: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  rejected: 'text-rose-700 bg-rose-50 border-rose-200',
  cancelled: 'text-slate-500 bg-slate-100 border-slate-200',
};
const fmtD = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });

const AdminLeavePanel = ({ token }) => {
  const [data, setData] = useState({ requests: [], pending_count: 0 });
  const [busyId, setBusyId] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/workforce/admin/leave`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await r.json());
    } catch { /* keep */ }
  }, [token]);
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, [load]);

  const act = async (id, action) => {
    setBusyId(id);
    try { await fetch(`${API}/api/workforce/admin/leave/${id}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: '{}' }); load(); }
    finally { setBusyId(null); }
  };

  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="workforce-leave-panel">
      <div className="flex items-center gap-2 px-5 pt-4 pb-3 border-b border-slate-100">
        <Plane className="w-4 h-4 text-[#3A6AFF]" />
        <h2 className="text-[15px] font-semibold">Leave &amp; Absence</h2>
        {data.pending_count > 0 && (
          <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-testid="leave-pending-count">{data.pending_count} pending</span>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {(data.requests || []).length === 0 && <p className="px-5 py-5 text-[13px] text-slate-400">No leave requests.</p>}
        {(data.requests || []).map((r) => (
          <div key={r.id} className="flex items-center gap-3 px-5 py-3" data-testid={`admin-leave-row-${r.id}`}>
            <span className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', r.type === 'sick' ? 'text-rose-600 bg-rose-50' : 'text-[#3A6AFF] bg-blue-50')}>
              {r.type === 'sick' ? <Thermometer className="w-4 h-4" /> : <Plane className="w-4 h-4" />}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-slate-800 truncate">{r.user_name}
                <span className="font-normal text-slate-500"> · {r.type_label}{r.is_sick_today ? ' (today)' : ''}</span>
              </p>
              <p className="text-[11.5px] text-slate-400 truncate">{fmtD(r.start_date)}{r.end_date !== r.start_date ? ` – ${fmtD(r.end_date)}` : ''}{r.half_day ? ` (${r.half_day === 'am' ? 'morning' : 'afternoon'})` : ''}{r.note ? ` · ${r.note}` : ''}</p>
            </div>
            {r.status === 'pending' ? (
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" disabled={busyId === r.id} onClick={() => act(r.id, 'approve')}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-lg text-[12px] font-semibold text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60" data-testid={`leave-approve-${r.id}`}>
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
                <button type="button" disabled={busyId === r.id} onClick={() => act(r.id, 'reject')}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-lg text-[12px] font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 disabled:opacity-60" data-testid={`leave-reject-${r.id}`}>
                  <X className="w-3.5 h-3.5" /> Decline
                </button>
              </div>
            ) : (
              <span className={cn('text-[11px] font-semibold px-2 py-0.5 rounded-full border capitalize shrink-0', LEAVE_STATUS[r.status])}>{r.status}</span>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const Col = ({ label, value }) => (
  <div className="text-center w-14">
    <p className="text-[9px] uppercase tracking-wider text-slate-400">{label}</p>
    <p className="font-semibold text-slate-700 tabular-nums">{value}</p>
  </div>
);

const SUM_TONE = {
  emerald: 'text-emerald-600 bg-emerald-50', amber: 'text-amber-600 bg-amber-50',
  slate: 'text-slate-600 bg-slate-100', blue: 'text-[#3A6AFF] bg-blue-50', violet: 'text-violet-600 bg-violet-50',
};
const SummaryCard = ({ icon: Icon, label, value, tone }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3.5" data-testid={`summary-${label}`}>
    <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center mb-2', SUM_TONE[tone])}><Icon className="w-4 h-4" /></div>
    <p className="text-[24px] font-bold text-slate-900 leading-none tabular-nums">{value}</p>
    <p className="text-[11px] text-slate-400 mt-1.5">{label}</p>
  </div>
);

const EVENT_TONE = {
  check_in: 'text-emerald-600 bg-emerald-50', break_start: 'text-amber-600 bg-amber-50',
  break_end: 'text-amber-600 bg-amber-50', finish: 'text-slate-600 bg-slate-100',
  idle: 'text-orange-500 bg-orange-50', module: 'text-[#3A6AFF] bg-blue-50',
};

const EmployeeDrawer = ({ userId, onClose, token }) => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/workforce/admin/employee/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await r.json());
    } catch { /* keep */ }
  }, [userId, token]);

  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);

  const s = data?.session;
  const u = data?.user;

  return (
    <div className="fixed inset-0 z-[80] flex justify-end" data-testid="workforce-employee-drawer">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[560px] h-full bg-[#FAFAFB] shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#5A8BFF] to-[#3A6AFF] flex items-center justify-center text-[13px] font-bold text-white">
            {u?.avatar_url ? <img src={u.avatar_url} alt={u.name} className="w-10 h-10 rounded-full object-cover" /> : initials(u?.name)}
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="text-[16px] font-bold text-slate-900 truncate">{u?.name || 'Employee'}</h3>
            <p className="text-[12px] text-slate-400">{s ? (STATUS_META[s.status] || STATUS_META.off).label : 'Not checked in'}{s?.active_now ? ' · active now' : ''}</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="drawer-close"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        {!s ? (
          <div className="p-8 text-center text-slate-400"><Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />Not checked in today.</div>
        ) : (
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-3 gap-3" data-testid="drawer-stats">
              <DStat icon={CheckCircle2} label="Checked In" value={fmtTime(s.checked_in_at)} />
              <DStat icon={Clock} label="Worked" value={s.work_label} />
              <DStat icon={Coffee} label="Break" value={s.break_label} />
              <DStat icon={Zap} label="Active" value={s.active_label} />
              <DStat icon={Activity} label="Idle" value={s.idle_label} />
              <DStat icon={Gauge} label="Productivity" value={`${s.productivity}%`} />
            </div>

            <section className="rounded-xl border border-slate-200 bg-white">
              <h4 className="text-[14px] font-semibold px-5 pt-4 pb-3 border-b border-slate-100">DerwentAI Activity</h4>
              <div className="p-5 space-y-2.5">
                {(s.modules || []).length === 0 && <p className="text-[13px] text-slate-400">No modules used yet.</p>}
                {(s.modules || []).map((m) => (
                  <div key={m.module} className="flex items-center justify-between text-[13px]">
                    <span className="text-slate-700">{m.module}</span>
                    <span className="font-semibold text-slate-900 tabular-nums">{m.label}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-xl border border-slate-200 bg-white">
              <h4 className="text-[14px] font-semibold px-5 pt-4 pb-3 border-b border-slate-100">Timeline</h4>
              <div className="p-5 space-y-3 max-h-[320px] overflow-y-auto">
                {(s.events || []).length === 0 && <p className="text-[13px] text-slate-400">No activity yet.</p>}
                {(s.events || []).map((e) => (
                  <div key={e.id} className="flex items-start gap-3">
                    <span className="text-[12px] tabular-nums text-slate-400 w-14 shrink-0 pt-0.5">{fmtTime(e.at)}</span>
                    <span className={cn('w-6 h-6 rounded-md flex items-center justify-center shrink-0', EVENT_TONE[e.type] || 'text-slate-500 bg-slate-100')}><Activity className="w-3 h-3" /></span>
                    <span className="text-[13px] text-slate-700 pt-0.5">{e.label}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-5">
              <div className="flex items-center gap-2 text-slate-500"><Monitor className="w-4 h-4" /><span className="text-[13px] font-semibold">Applications & Websites</span></div>
              <p className="text-[12px] text-slate-400 mt-2">Desktop app / website visibility requires the DerwentAI Desktop Agent. <span className="font-medium text-slate-500">Coming soon.</span></p>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

const DStat = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
    <div className="flex items-center gap-1.5 text-slate-400 mb-1"><Icon className="w-3.5 h-3.5" /><span className="text-[9.5px] font-semibold uppercase tracking-wider">{label}</span></div>
    <p className="text-[16px] font-bold text-slate-900 tabular-nums">{value}</p>
  </div>
);

export default Workforce;
